/** @jsx jsx */
import { AddIcon, Button, DatePicker, Flex } from "@eworkit/iris";
import React from "react";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { TimeReport, useTimeReportAggregate } from "@/data/time-reports";
import { useFormats } from "@/hooks/Formats";
import { TimeReportProjectModal } from "@/views/time-reports/projects";

import { TimeReportCalendarGrid } from "./TimeReportCalendarGrid";
import { useTimeReportCalendarNavigation } from "./useTimeReportCalendarNavigation";

interface TimeReportCalendarMobileProps {
  timeReport: TimeReport;
  editable?: boolean;
}

export const TimeReportCalendarMobile: React.FC<TimeReportCalendarMobileProps> = ({ timeReport, editable }) => {
  const { t } = useTranslation("session");

  const { formatDate } = useFormats();

  const { days, currentDay: day, canGoToToday, goToDate, goToToday } = useTimeReportCalendarNavigation(timeReport);

  const aggregateData = useTimeReportAggregate(timeReport, {
    from: day,
    to: day,
  });

  const addProjectModalState = useDialogState({ animated: true });

  return (
    <React.Fragment>
      <Flex flexDirection="column" gap="sm">
        {editable && (
          <Button as={DialogDisclosure} {...addProjectModalState} variant="secondary" size="sm" iconLeft={<AddIcon />}>
            {t("TIME_REPORTS.PROJECTS.ADD_MODAL_HEADER")}
          </Button>
        )}

        <Flex gap="xs" alignItems="center">
          {canGoToToday && (
            <Button onClick={goToToday} size="sm" variant="secondary">
              {t("TIME_REPORTS.CALENDAR.TODAY")}
            </Button>
          )}

          <Flex flexGrow={1}>
            <DatePicker size="md" value={day} onChange={goToDate} includeDates={days}>
              {formatDate(day.toISOString())}
            </DatePicker>
          </Flex>
        </Flex>

        <TimeReportCalendarGrid timeReport={timeReport} aggregateData={aggregateData} editable={editable} />
      </Flex>

      {editable && <TimeReportProjectModal dialogState={addProjectModalState} timeReport={timeReport} />}
    </React.Fragment>
  );
};
